/**
 * React Query configuration.
 *
 * Two deliberate choices:
 *
 * * **Do not retry client errors.** A 401/403/404/409 will not fix itself, and
 *   retrying a 409 during the reservation race just delays the honest answer.
 *   Server and network errors get one retry.
 * * **Keep catalog data fresh but not chatty.** Availability changes when staff
 *   act, so a minute of staleness is fine for browsing while mutations
 *   invalidate the affected scopes immediately.
 */

import { QueryClient } from '@tanstack/react-query'

import { ApiError } from './api'

const RETRYABLE_STATUSES = new Set([0, 408, 425, 429, 500, 502, 503, 504])

const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 1) return false
  if (error instanceof ApiError) return RETRYABLE_STATUSES.has(error.status)
  return true
}

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: false,
        // Availability can change between visits to a page, so refetch on mount
        // once the cached copy is stale.
        refetchOnMount: true,
      },
      mutations: {
        retry: false,
      },
    },
  })
